import React, {
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  formatPhone,
  notEmptyString,
} from '@populateui/utils';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import Button from 'pages/Dashboard/components/Button';
import Dialog from 'pages/Dashboard/components/Dialog';
import Name from 'pages/Dashboard/components/Name';
import Skeleton from 'core/components/Skeleton';
import StatusTag from 'core/components/StatusTag';
import {
  DEFAULT_EMPTY_VALUE,
} from 'core/constants';
import {
  CallDetailsDTO,
} from 'dtos';
import {
  CcmMonthlyCallRow,
} from 'pages/Dashboard/pages/Dashboard/CcmMonthly/types';
import {
  useGetCcmCallDetails,
} from 'pages/Dashboard/pages/Dashboard/CcmMonthly/hooks';
import {
  formatDuration,
} from 'pages/Dashboard/pages/Dashboard/CcmMonthly/helper';
import {
  getPatientChartPath,
} from 'pages/Dashboard/pages/Charts/utils';
import {
  formatDateTime,
  formatTime,
} from 'utils/date';

type Props = {
  callInfo?: CcmMonthlyCallRow | null;
  onClose: () => void;
};

const CCM_CALL_DETAIL_STATUS_MAP = Object.freeze({
  success: 'completed',
  pending: 'pending',
  error: 'failed',
  inprogress: 'in progress',
});
const SYSTEM_TRANSCRIPT_USERS: readonly string[] = ['agent', 'assistant', 'system', 'bot'];

type DetailItemProps = {
  label: string;
  value?: string | null;
};

function DetailItem({ label, value }: DetailItemProps) {
  return !notEmptyString(value) ? null : (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="text-[10px] font-medium uppercase tracking-wide text-gray-500">{label}</span>
      <span className="text-xs font-semibold text-gray-800 break-words">{value}</span>
    </div>
  );
}

function DetailSkeleton() {
  const detailSkeletonKeys = ['patient', 'call-date', 'duration', 'called-number', 'status'] as const;

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col gap-4">
      <section className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5 rounded-lg border border-gray-200 bg-white p-4">
        {detailSkeletonKeys.map((key, index) => (
          <div key={key} className="flex min-w-0 flex-col gap-2">
            <Skeleton variant="rounded" className="!h-2.5 !w-16 !rounded-full" />
            <Skeleton
              variant="rounded"
              className={index === 0 ? '!h-4 !w-40 !rounded-full' : '!h-4 !w-24 !rounded-full'}
            />
          </div>
        ))}
      </section>

      <section className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex flex-col gap-2">
          <Skeleton variant="rounded" className="!h-5 !w-24 !rounded-full" />
        </div>
        <Skeleton variant="rounded" className="mt-4 !h-[3.25rem] !w-full !rounded-full" />
      </section>

      <section className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="mb-4">
          <Skeleton variant="rounded" className="!h-5 !w-24 !rounded-full" />
        </div>
        <div className="flex flex-col gap-5">
          <div className="flex justify-end">
            <div className="flex w-full max-w-[7rem] flex-col items-end gap-2">
              <Skeleton variant="rounded" className="!h-10 !w-full !rounded-2xl" />
              <Skeleton variant="rounded" className="!h-3 !w-20 !rounded-full" />
            </div>
          </div>
          <div className="flex justify-start">
            <div className="flex w-full max-w-[38rem] flex-col gap-2">
              <Skeleton variant="rounded" className="!h-11 !w-full !rounded-2xl" />
              <Skeleton variant="rounded" className="!h-3 !w-24 !rounded-full" />
            </div>
          </div>
          <div className="flex justify-end">
            <div className="flex w-full max-w-[10rem] flex-col items-end gap-2">
              <Skeleton variant="rounded" className="!h-10 !w-full !rounded-2xl" />
              <Skeleton variant="rounded" className="!h-3 !w-20 !rounded-full" />
            </div>
          </div>
          <div className="flex justify-start">
            <div className="flex w-full max-w-[46rem] flex-col gap-2">
              <Skeleton variant="rounded" className="!h-24 !w-full !rounded-2xl" />
              <Skeleton variant="rounded" className="!h-3 !w-24 !rounded-full" />
            </div>
          </div>
          <div className="flex justify-end">
            <div className="flex w-full max-w-[9rem] flex-col items-end gap-2">
              <Skeleton variant="rounded" className="!h-10 !w-full !rounded-2xl" />
              <Skeleton variant="rounded" className="!h-3 !w-20 !rounded-full" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function ErrorState() {
  return (
    <div className="flex h-40 items-center justify-center rounded-lg border border-red-200 bg-red-50 text-sm text-red-600">
      Unable to load CCM call details.
    </div>
  );
}

function isAgentTranscript(user?: string | null): boolean {
  return SYSTEM_TRANSCRIPT_USERS.includes(user?.toLowerCase() ?? '');
}

function getAudioSrc(callId?: string | null, recordingUrl?: string | null): string {
  if (notEmptyString(recordingUrl)) {
    return recordingUrl ?? '';
  }

  if (notEmptyString(callId)) {
    return `/api/Bland/GetCallRecording?callId=${encodeURIComponent(callId ?? '')}`;
  }

  return recordingUrl ?? '';
}

function getCallDateValue(callInfo?: CcmMonthlyCallRow | null, startedAt?: string): string | null {
  if (notEmptyString(startedAt)) {
    return formatDateTime(startedAt ?? '', false);
  }

  if (notEmptyString(callInfo?.calledOn)) {
    return formatDateTime(callInfo?.calledOn ?? '');
  }

  return null;
}

function getDurationValue(callInfo?: CcmMonthlyCallRow | null, correctedDuration?: string | null, callLength?: number): string | null {
  const correctedDurationSeconds = Number(correctedDuration);

  if (notEmptyString(correctedDuration) && !Number.isNaN(correctedDurationSeconds)) {
    return formatDuration(correctedDurationSeconds);
  }

  if (notEmptyString(correctedDuration)) {
    return correctedDuration ?? null;
  }

  if (!isNil(callLength)) {
    return formatDuration(callLength);
  }

  if (!isNil(callInfo?.durationSeconds)) {
    return formatDuration(callInfo.durationSeconds);
  }

  return null;
}

function getSortedTranscriptItems(
  transcripts?: CallDetailsDTO['transcripts'],
): NonNullable<CallDetailsDTO['transcripts']> {
  return (transcripts ?? []).slice().sort((left, right) => (
    new Date(left.created_at ?? '').getTime() - new Date(right.created_at ?? '').getTime()
  ));
}

type TranscriptContentProps = {
  patientName?: string | null;
  transcriptItems: NonNullable<CallDetailsDTO['transcripts']>;
  transcriptText?: string | null;
};

type TranscriptMessageListProps = Pick<
  TranscriptContentProps,
  'patientName' | 'transcriptItems'
>;

function TranscriptMessageList({
  patientName,
  transcriptItems,
}: TranscriptMessageListProps) {
  return (
    <div className="flex flex-col gap-3">
      {transcriptItems.map((item, index) => {
        const isAgent = isAgentTranscript(item.user);
        const speakerName = isAgent ? 'Rachel' : patientName ?? 'Patient';

        return (
          <div
            key={item.transcript_id ?? item.id ?? index}
            className={clsx('flex', {
              'justify-start': isAgent,
              'justify-end': !isAgent,
            })}
          >
            <div className="max-w-[80%]">
              <div
                className={clsx(
                  'rounded-2xl px-4 py-3 text-sm text-gray-800',
                  {
                    'rounded-tl-sm bg-blue-50': isAgent,
                    'rounded-tr-sm bg-gray-100': !isAgent,
                  },
                )}
              >
                {item.text ?? DEFAULT_EMPTY_VALUE}
              </div>
              <div
                className={clsx('mt-1', {
                  'text-left': isAgent,
                  'text-right': !isAgent,
                })}
              >
                <span className="text-[10px] font-medium uppercase tracking-wide text-gray-500">
                  {speakerName}
                </span>
                {notEmptyString(item.created_at) && (
                  <span className="ml-2 text-[10px] text-gray-400">
                    {formatTime(item.created_at ?? '')}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TranscriptContent({
  patientName,
  transcriptItems,
  transcriptText,
}: TranscriptContentProps) {
  const hasTranscriptText = notEmptyString(transcriptText);

  return transcriptItems.length > 0 ? (
    <TranscriptMessageList
      patientName={patientName}
      transcriptItems={transcriptItems}
    />
  ) : (
    <div
      className={clsx(
        'rounded-md bg-gray-50 text-sm',
        hasTranscriptText
          ? 'px-4 py-3 text-gray-700 whitespace-pre-wrap'
          : 'border border-dashed border-gray-200 px-4 py-6 text-gray-500',
      )}
    >
      {hasTranscriptText ? transcriptText : 'Transcript is not available for this call.'}
    </div>
  );
}

export default function CcmCallDetailDialog({
  callInfo,
  onClose,
}: Props) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const isOpen = !isNil(callInfo);
  const {
    data: callDetails,
    isFetching,
    isError,
  } = useGetCcmCallDetails(callInfo?.callId, isOpen);

  useEffect(() => {
    if (!isOpen) {
      audioRef.current?.pause();
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
      }
    }
  }, [isOpen]);

  const transcriptItems = useMemo(
    () => getSortedTranscriptItems(callDetails?.transcripts),
    [callDetails?.transcripts],
  );
  const audioSrc = getAudioSrc(callDetails?.call_id, callDetails?.recording_url);
  const callDate = getCallDateValue(callInfo, callDetails?.started_at);
  const duration = getDurationValue(callInfo, callDetails?.corrected_duration, callDetails?.call_length);
  const calledNumber = notEmptyString(callDetails?.to) ? formatPhone(callDetails?.to ?? '') : null;
  const transcriptText = callDetails?.concatenated_transcript;
  const isLoadingState = isFetching && isNil(callDetails);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      title="CCM Call Details"
      maxWidth={false}
      classes={{
        root: '!w-[min(64rem,calc(100vw-32px))] !h-[calc(100vh-48px)] !max-w-none min-h-0 flex flex-col !p-0 !space-y-0',
        bodyRoot: '!h-[calc(100vh-48px)] !overflow-hidden !rounded-xl',
        title: 'border-0 p-4 shrink-0 border-b border-gray-200',
      }}
    >
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex min-h-0 flex-1 overflow-hidden bg-gray-50 p-4">
          {isLoadingState && <DetailSkeleton />}
          {!isLoadingState && isError && <ErrorState />}
          {!isLoadingState && !isError && (
            <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden">
              <div className="shrink-0 space-y-4">
                <section className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5 rounded-lg border border-gray-200 bg-white p-4">
                  <div className="flex min-w-0 flex-col gap-0.5">
                    <span className="text-[10px] font-medium uppercase tracking-wide text-gray-500">Patient</span>
                    <Name
                      link={getPatientChartPath(callInfo?.patientId)}
                      name={callInfo?.patientName ?? DEFAULT_EMPTY_VALUE}
                      classes={{
                        root: '!min-w-0 !font-normal',
                        nameRoot: '!min-w-0',
                        label: '!text-xs !font-semibold !leading-5',
                      }}
                    />
                  </div>
                  <DetailItem label="Called at" value={callDate} />
                  <DetailItem label="Duration" value={duration} />
                  <DetailItem label="To" value={calledNumber} />
                  <div className="flex min-w-0 flex-col gap-0.5">
                    <span className="text-[10px] font-medium uppercase tracking-wide text-gray-500">Status</span>
                    <StatusTag
                      value={callDetails?.status ?? DEFAULT_EMPTY_VALUE}
                      statusMap={CCM_CALL_DETAIL_STATUS_MAP}
                      className="w-fit capitalize"
                    />
                  </div>
                </section>

                <section className="rounded-lg border border-gray-200 bg-white p-4">
                  <div className="text-sm font-semibold text-gray-800">Recording</div>
                  {notEmptyString(audioSrc) ? (
                    <audio
                      ref={audioRef}
                      key={audioSrc}
                      className="mt-4 w-full"
                      controls
                      preload="metadata"
                      src={audioSrc}
                    />
                  ) : (
                    <div className="mt-4 rounded-md border border-dashed border-gray-200 bg-gray-50 px-4 py-3 text-xs text-gray-500">
                      Recording is not available for this call.
                    </div>
                  )}
                </section>
              </div>

              <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white p-4">
                <div className="mb-4 shrink-0 flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-gray-800">Transcript</div>
                  </div>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto pr-1">
                  <TranscriptContent
                    patientName={callInfo?.patientName}
                    transcriptItems={transcriptItems}
                    transcriptText={transcriptText}
                  />
                </div>
              </section>
            </div>
          )}
        </div>
        <div className="flex shrink-0 justify-end border-t border-gray-200 bg-white px-4 py-3">
          <Button type="button" variant="outlined" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
